// CreditEditor — POST (create) / PATCH (edit % or type) / Replace
// (DELETE + POST). Implements F-3, F-002 cache invalidation, BR-04
// blocking modal handling.

import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import {
  creditsApi,
  peopleApi,
  type CreditCreateBody,
  type CreditPatchBody,
} from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { useDirtyFormReloadGuard } from '@/utils/useDirtyFormReloadGuard';
import { ApiError } from '@/types/errors';
import type { Assignment, Credit, CreditType, Person } from '@/types/domain';

interface Props {
  open: boolean;
  onClose: () => void;
  assignment: Assignment;
  existing: Credit | null;
  mode: 'create' | 'patch' | 'replace';
  forcedCreditType?: CreditType;
  onChanged: () => void;
}

interface FormShape {
  person_id: string;
  credit_type: CreditType;
  percentage: string;
}

const KNOWN: string[] = ['person_id', 'credit_type', 'percentage'];

export function CreditEditor({
  open,
  onClose,
  assignment,
  existing,
  mode,
  forcedCreditType,
  onChanged,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
    enabled: open,
  });
  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [peopleQ.data]);

  // After a Replace flow's step-2 (POST) fails, the original credit has
  // already been DELETEd by step 1. We flip into `'create'` mode so the
  // user's resubmit does not re-attempt the (now-404) DELETE. The
  // partial-failure banner surfaces an [Add manually] affordance that
  // pre-fills percentage/credit_type from the original and focuses the
  // person picker.
  const [effectiveMode, setEffectiveMode] = useState<typeof mode>(mode);
  const [effectiveExisting, setEffectiveExisting] = useState<Credit | null>(existing);

  // If parent re-opens the editor with a new mode/existing pair, mirror.
  useEffect(() => {
    setEffectiveMode(mode);
    setEffectiveExisting(existing);
  }, [mode, existing]);

  const defaults = useMemo<FormShape>(
    () =>
      effectiveExisting
        ? {
            person_id: effectiveExisting.person_id,
            credit_type: effectiveExisting.credit_type,
            percentage: String(effectiveExisting.percentage),
          }
        : {
            person_id: '',
            credit_type: forcedCreditType ?? 'sales',
            percentage: '',
          },
    [effectiveExisting, forcedCreditType],
  );

  const {
    register,
    handleSubmit,
    setError,
    reset,
    setFocus,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<FormShape>({ defaultValues: defaults });

  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  // Browser reload-warning safety net per G-FE-1.
  useDirtyFormReloadGuard(open && isDirty);

  const [blockingDetails, setBlockingDetails] = useState<{
    current: string;
    attempted: string;
    would: string;
  } | null>(null);
  const [partialFailure, setPartialFailure] = useState<{
    message: string;
    // Snapshot of the original credit_type + percentage so the
    // [Add manually] button can pre-fill the converted-to-create form.
    pendingCreditType: CreditType;
    pendingPercentage: number;
    pendingPersonId: string;
  } | null>(null);

  const createM = useMutation({
    mutationFn: (body: CreditCreateBody) =>
      creditsApi.create(assignment.id, body),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreditPatchBody }) =>
      creditsApi.update(id, body),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => creditsApi.delete(id),
  });

  // [Add manually] handler: flip the form into create mode pre-filled
  // with the original credit_type + percentage and the user's chosen
  // person_id, drop the now-stale `existing` reference, and focus the
  // primary submit so a single Enter / click recovers the data.
  const onAddManually = () => {
    if (!partialFailure) return;
    setEffectiveMode('create');
    setEffectiveExisting(null);
    reset({
      person_id: partialFailure.pendingPersonId,
      credit_type: partialFailure.pendingCreditType,
      percentage: String(partialFailure.pendingPercentage),
    });
    // Clear the banner; the next submit runs the create-only path.
    setPartialFailure(null);
    // Focus the primary action so Enter completes recovery.
    window.queueMicrotask(() => {
      try {
        setFocus('person_id');
      } catch {
        /* form may have unmounted */
      }
    });
  };

  const onSubmit: SubmitHandler<FormShape> = async (values) => {
    setBlockingDetails(null);
    setPartialFailure(null);
    try {
      if (effectiveMode === 'patch' && effectiveExisting) {
        const body: CreditPatchBody = {};
        if (dirtyFields.credit_type) body.credit_type = values.credit_type;
        if (dirtyFields.percentage)
          body.percentage = Number(values.percentage);
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await patchM.mutateAsync({ id: effectiveExisting.id, body });
        toast.show({ severity: 'success', message: 'Credit saved.' });
      } else if (effectiveMode === 'replace' && effectiveExisting) {
        // DELETE + POST. If the second call fails, the original is
        // already gone; we drop into create mode so the next submit
        // POSTs a fresh row without re-running the DELETE on a
        // no-longer-existing credit.
        const original = effectiveExisting;
        await deleteM.mutateAsync(original.id);
        try {
          await createM.mutateAsync({
            person_id: values.person_id,
            credit_type: original.credit_type,
            percentage: original.percentage,
          });
          toast.show({ severity: 'success', message: 'Credit replaced.' });
        } catch (err) {
          const message =
            err instanceof ApiError && err.message
              ? `Replaced original credit but couldn't create the new one: ${err.message}`
              : "Replaced original credit but couldn't create the new one.";
          setPartialFailure({
            message,
            pendingCreditType: original.credit_type,
            pendingPercentage: original.percentage,
            pendingPersonId: values.person_id,
          });
          // Drop the now-deleted credit from the form's state so the
          // canClose / dirty guard reflect a fresh-create posture.
          setEffectiveExisting(null);
          // Refresh the surrounding list immediately — the DELETE
          // already mutated server state.
          void qc.invalidateQueries({
            queryKey: qk.assignmentCredits(assignment.id),
          });
          onChanged();
          // Do not close the modal; recovery happens via [Add manually].
          return;
        }
      } else {
        await createM.mutateAsync({
          person_id: values.person_id,
          credit_type: values.credit_type,
          percentage: Number(values.percentage),
        });
        toast.show({ severity: 'success', message: 'Credit added.' });
      }
      void qc.invalidateQueries({
        queryKey: qk.assignmentCredits(assignment.id),
      });
      onChanged();
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, KNOWN);
      for (const [field, message] of Object.entries(mapped.fieldErrors)) {
        setError(field as keyof FormShape, { type: 'server', message });
      }
      if (
        mapped.blockingModal === 'managed_credit_exceeds_100' &&
        err instanceof ApiError
      ) {
        setBlockingDetails({
          current: String(err.details['current_managed_total'] ?? '?'),
          attempted: String(err.details['attempted_percentage'] ?? '?'),
          would: String(err.details['would_become'] ?? '?'),
        });
      } else if (mapped.formMessage) {
        toast.show({ severity: 'error', message: mapped.formMessage });
      }
    }
  };


  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        effectiveMode === 'patch'
          ? 'Edit credit'
          : effectiveMode === 'replace'
            ? 'Replace credit person'
            : 'Add credit'
      }
      role="dialog"
      canClose={() => !isDirty || window.confirm('Discard unsaved changes?')}
    >
      {partialFailure ? (
        <div className="inline-error" role="alert">
          <p style={{ margin: '0 0 8px 0' }}>{partialFailure.message}</p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onAddManually}
          >
            Add manually
          </button>
        </div>
      ) : null}
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="form-field">
          <label htmlFor="ce-person">
            Person<span className="form-required" aria-hidden="true">*</span>
          </label>
          {effectiveMode === 'patch' && effectiveExisting ? (
            <input
              value={
                peopleById.get(effectiveExisting.person_id)?.name ??
                effectiveExisting.person_id
              }
              readOnly
              disabled
            />
          ) : (
            <select
              id="ce-person"
              disabled={isSubmitting || peopleQ.isPending}
              {...register('person_id', { required: 'Choose a person.' })}
            >
              <option value="">Select a person…</option>
              {peopleQ.data?.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.email})
                </option>
              ))}
            </select>
          )}
          {errors.person_id ? (
            <span className="form-error">{errors.person_id.message}</span>
          ) : null}
        </div>

        {effectiveMode === 'replace' ? null : forcedCreditType ? (
          <input type="hidden" {...register('credit_type')} />
        ) : (
          <fieldset className="form-field">
            <legend>
              Credit type
              <span className="form-required" aria-hidden="true">*</span>
            </legend>
            <label style={{ marginRight: 16 }}>
              <input type="radio" value="sales" {...register('credit_type')} /> Sales
            </label>
            <label>
              <input type="radio" value="managed" {...register('credit_type')} /> Managed
            </label>
          </fieldset>
        )}

        {effectiveMode === 'replace' ? null : (
          <div className="form-field">
            <label htmlFor="ce-pct">
              Percentage<span className="form-required" aria-hidden="true">*</span>
            </label>
            <input
              id="ce-pct"
              type="number"
              min={1}
              max={100}
              step={1}
              disabled={isSubmitting}
              {...register('percentage', {
                required: 'Percentage is required.',
                validate: (v) => {
                  const n = Number(v);
                  if (!Number.isInteger(n)) return 'Whole percent only.';
                  if (n < 1 || n > 100) return 'Must be between 1 and 100.';
                  return true;
                },
              })}
            />
            {errors.percentage ? (
              <span className="form-error">{errors.percentage.message}</span>
            ) : null}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
            aria-busy={isSubmitting ? 'true' : 'false'}
          >
            {isSubmitting
              ? 'Saving…'
              : effectiveMode === 'replace'
                ? 'Replace credit'
                : effectiveMode === 'patch'
                  ? 'Save'
                  : 'Add credit'}
          </button>
        </div>
      </form>

      {blockingDetails ? (
        <Modal
          open={true}
          onClose={() => setBlockingDetails(null)}
          title="Managed credit limit exceeded"
          role="alertdialog"
          scrimDismissDisabled
        >
          <p>
            Saving this credit would bring Managed Credit on this assignment to{' '}
            <strong>{blockingDetails.would}%</strong>, which exceeds the 100% limit.
            (Current managed total: {blockingDetails.current}%; this entry: {blockingDetails.attempted}%.)
          </p>
          <p>Reduce another Managed Credit entry or lower this one.</p>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setBlockingDetails(null)}
            >
              Lower this entry
            </button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
